import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import companyAccountsService from '../../services/companyAccountsService';
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard';
import ClientOverviewTable from './components/ClientOverviewTable';
import FilterPanel from './components/FilterPanel';
import AlertsPanel from './components/AlertsPanel';
import BulkOperationsModal from './components/BulkOperationsModal';

const MultiClientOrganizationOverviewDashboard = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [filters, setFilters] = useState({
    setupStatus: 'all',
    complianceScore: 'all',
    billingStatus: 'all',
    region: 'all',
    searchTerm: ''
  });

  const handleGlobalFilterChange = (globalFilters) => {
    // Handle global filter changes if needed
    console.log('Global filters changed:', globalFilters);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [clients, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [clientsData, summaryData] = await Promise.all([
        companyAccountsService?.getAllCompanyAccounts(),
        companyAccountsService?.getExecutiveSummary()
      ]);

      // Calculate setup progress for each client
      const clientsWithProgress = clientsData?.map(client => ({
        ...client,
        setupProgress: calculateSetupProgress(client)
      }));

      setClients(clientsWithProgress);
      setSummary(summaryData);
    } catch (err) {
      setError(err?.message || 'Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const calculateSetupProgress = (client) => {
    let completed = 0;
    const total = 4;

    // Organization profile complete
    if (client?.companyName && client?.contactEmail) completed++;
    // Bank integration (check if subscription active)
    if (client?.subscriptionStatus === 'active') completed++;
    // Payroll configuration (check if employees exist)
    if (client?.employeeCount > 0) completed++;
    // System activation (check if compliance score exists)
    if (client?.complianceScore > 0) completed++;

    return Math.round((completed / total) * 100);
  };

  const applyFilters = () => {
    let filtered = [...clients];

    // Search filter
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(client =>
        client?.companyName?.toLowerCase()?.includes(term) ||
        client?.contactEmail?.toLowerCase()?.includes(term)
      );
    }

    // Setup status filter
    if (filters?.setupStatus !== 'all') {
      filtered = filtered?.filter(client => {
        const progress = client?.setupProgress || 0;
        if (filters?.setupStatus === 'complete') return progress === 100;
        if (filters?.setupStatus === 'partial') return progress >= 50 && progress < 100;
        if (filters?.setupStatus === 'incomplete') return progress < 50;
        return true;
      });
    }

    // Compliance score filter
    if (filters?.complianceScore !== 'all') {
      filtered = filtered?.filter(client => {
        const score = client?.complianceScore || 0;
        if (filters?.complianceScore === 'excellent') return score >= 90;
        if (filters?.complianceScore === 'good') return score >= 70 && score < 90;
        if (filters?.complianceScore === 'warning') return score < 70;
        return true;
      });
    }

    // Billing status filter
    if (filters?.billingStatus !== 'all') {
      filtered = filtered?.filter(client => {
        if (!client?.nextBillingDate) return filters?.billingStatus === 'overdue';
        const daysUntilBilling = Math.floor(
          (new Date(client?.nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)
        );
        if (filters?.billingStatus === 'current') return daysUntilBilling > 7;
        if (filters?.billingStatus === 'overdue') return daysUntilBilling < 0;
        if (filters?.billingStatus === 'suspended') return client?.subscriptionStatus === 'suspended';
        return true;
      });
    }

    setFilteredClients(filtered);
  };

  const handleClientAction = (client, action) => {
    console.log(`Action: ${action} for client:`, client?.companyName);
    // Implement specific actions: view, billing, support, suspend
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => {
      if (prev?.includes(clientId)) {
        return prev?.filter(id => id !== clientId);
      }
      return [...prev, clientId];
    });
  };

  const handleSelectAll = () => {
    if (selectedClients?.length === filteredClients?.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients?.map(c => c?.id));
    }
  };

  const handleBulkOperation = (operation) => {
    console.log(`Bulk operation: ${operation} for ${selectedClients?.length} clients`);
    setShowBulkModal(true);
  };

  const handleExport = () => {
    console.log('Exporting client overview report...');
    // Implement export functionality
  };

  const handleRefresh = () => {
    loadData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
        <ComplianceAlertBanner />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading client organizations...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />

      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Multi-Client Organization Overview Dashboard
            </h1>
            <p className="text-muted-foreground">
              Centralized monitoring and management of all client organizations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefresh}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
            <Button onClick={handleExport}>
              <Icon name="Download" size={16} className="mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-error font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Executive Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <ExecutiveSummaryCard
            title="Total Active Clients"
            value={summary?.activeAccounts || 0}
            subtitle={`${clients?.length || 0} total organizations`}
            icon="Building2"
            trend="up"
            status="compliant"
          />
          <ExecutiveSummaryCard
            title="Average Compliance Score"
            value={`${Math.round(summary?.avgComplianceScore || 0)}%`}
            subtitle="PNG Employment Act adherence"
            icon="Shield"
            trend={summary?.avgComplianceScore >= 80 ? 'up' : 'down'}
            status={summary?.avgComplianceScore >= 80 ? 'compliant' : 'warning'}
          />
          <ExecutiveSummaryCard
            title="Billing Health"
            value={`${summary?.billingHealthPercentage || 0}%`}
            subtitle="Current payment status"
            icon="CreditCard"
            trend={summary?.billingHealthPercentage >= 80 ? 'up' : 'down'}
            status={summary?.billingHealthPercentage >= 80 ? 'compliant' : 'critical'}
          />
          <ExecutiveSummaryCard
            title="Support Tickets"
            value={summary?.criticalIssues || 0}
            subtitle="Requiring attention"
            icon="Headphones"
            trend="down"
            status={summary?.criticalIssues > 5 ? 'critical' : 'compliant'}
          />
        </div>

        {/* Real-time Alert System */}
        <AlertsPanel clients={filteredClients} />

        {/* Filter Panel */}
        <FilterPanel
          filters={filters}
          onFilterChange={setFilters}
          totalClients={clients?.length}
          filteredCount={filteredClients?.length}
        />

        {/* Bulk Operations Bar */}
        {selectedClients?.length > 0 && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="CheckSquare" size={20} className="text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {selectedClients?.length} client{selectedClients?.length > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkOperation('policy_update')}
                >
                  <Icon name="FileText" size={14} className="mr-1" />
                  Update Policies
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkOperation('compliance_reminder')}
                >
                  <Icon name="Bell" size={14} className="mr-1" />
                  Send Reminders
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkOperation('announcement')}
                >
                  <Icon name="Megaphone" size={14} className="mr-1" />
                  Announcement
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedClients([])}
                >
                  <Icon name="X" size={14} />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main Client Overview Table */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-foreground">Client Organizations</h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                <Icon name="CheckSquare" size={14} className="mr-1" />
                {selectedClients?.length === filteredClients?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>
          <ClientOverviewTable
            clients={filteredClients}
            selectedClients={selectedClients}
            onSelectClient={handleSelectClient}
            onClientAction={handleClientAction}
          />
        </div>
      </main>

      {/* Bulk Operations Modal */}
      {showBulkModal && (
        <BulkOperationsModal
          selectedClients={selectedClients}
          clients={clients}
          onClose={() => setShowBulkModal(false)}
          onComplete={() => {
            setShowBulkModal(false);
            setSelectedClients([]);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default MultiClientOrganizationOverviewDashboard;